import { Badge, Button, Group, Modal, PasswordInput, Stack, Table, TextInput, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { type Schemas, api, call } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert, Loading, formatTs, notifyError, notifyOk } from "../components/common";

/** Platform administration: companies (tenants) and their first administrator. */
export function PlatformPage() {
  const { actAs, actingCompany } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [opened, { open, close }] = useDisclosure();
  const [adminFor, setAdminFor] = useState<Schemas["CompanyOut"] | null>(null);
  const [company, setCompany] = useState({ name: "", slug: "" });
  const [admin, setAdmin] = useState({ email: "", full_name: "", password: "" });

  // Platform endpoints must be called without a company context.
  const companies = useQuery({
    queryKey: ["platform-companies"],
    enabled: !actingCompany,
    queryFn: () => call(api.GET("/api/v1/platform/companies")),
  });
  const create = useMutation({
    mutationFn: () => call(api.POST("/api/v1/platform/companies", { body: company })),
    onSuccess: () => { notifyOk("Company created"); void qc.invalidateQueries({ queryKey: ["platform-companies"] }); close(); },
    onError: notifyError,
  });
  const createAdmin = useMutation({
    mutationFn: () => call(api.POST("/api/v1/platform/companies/{company_id}/admins", {
      params: { path: { company_id: adminFor!.id } }, body: admin,
    })),
    onSuccess: () => { notifyOk("Company administrator created"); setAdminFor(null); },
    onError: notifyError,
  });

  if (actingCompany) {
    return (
      <Stack>
        <Title order={2}>Platform administration</Title>
        <Button w={260} onClick={() => void actAs(null)}>Leave {actingCompany.name}</Button>
      </Stack>
    );
  }
  if (companies.isLoading) return <Loading />;
  if (companies.error) return <ErrorAlert error={companies.error} />;
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Companies</Title>
        <Button onClick={open}>New company</Button>
      </Group>
      <Table.ScrollContainer minWidth={700}>
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Name</Table.Th><Table.Th>Slug</Table.Th><Table.Th>Plan</Table.Th><Table.Th>Status</Table.Th><Table.Th>Created</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {companies.data!.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>{c.name}</Table.Td><Table.Td>{c.slug}</Table.Td><Table.Td>{c.plan}</Table.Td>
                <Table.Td><Badge color={c.status === "active" ? "green" : "red"}>{c.status}</Badge></Table.Td>
                <Table.Td>{formatTs(c.created_at)}</Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="end">
                    <Button size="xs" variant="light" onClick={() => setAdminFor(c)}>Add admin</Button>
                    <Button size="xs" variant="default" onClick={async () => { await actAs({ id: c.id, name: c.name }); navigate("/"); }}>
                      Open as admin
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal opened={opened} onClose={close} title="New company">
        <Stack>
          <TextInput label="Name" required value={company.name} onChange={(e) => setCompany({ ...company, name: e.currentTarget.value })} />
          <TextInput label="Slug" description="lowercase, digits and dashes" required value={company.slug}
            onChange={(e) => setCompany({ ...company, slug: e.currentTarget.value })} />
          <Button loading={create.isPending} onClick={() => create.mutate()}>Create</Button>
        </Stack>
      </Modal>
      <Modal opened={!!adminFor} onClose={() => setAdminFor(null)} title={`Administrator for ${adminFor?.name ?? ""}`}>
        <Stack>
          <TextInput label="Full name" required value={admin.full_name} onChange={(e) => setAdmin({ ...admin, full_name: e.currentTarget.value })} />
          <TextInput label="Email" required value={admin.email} onChange={(e) => setAdmin({ ...admin, email: e.currentTarget.value })} />
          <PasswordInput label="Initial password" required value={admin.password} onChange={(e) => setAdmin({ ...admin, password: e.currentTarget.value })} />
          <Button loading={createAdmin.isPending} onClick={() => createAdmin.mutate()}>Create administrator</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
